import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Typography } from '@mui/material';
import WebApi from '../services/webApi';

export const readVerificationToken = () => new URLSearchParams(window.location.hash.slice(1)).get('verify');

// Shared by the trial prompt and account settings, including legacy accounts.
export function EmailVerificationPanel({ email, onVerified, initialDelivery, forTrial = true }) {
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(initialDelivery?.sent
    ? 'A verification email is on its way. Check your inbox and spam folder.' : '');
  const [retryUntil, setRetryUntil] = useState(initialDelivery?.retryAfter ? Date.now() + initialDelivery.retryAfter * 1000 : 0);
  const [now, setNow] = useState(Date.now());
  const remaining = Math.max(0, Math.ceil((retryUntil - now) / 1000));

  useEffect(() => {
    if (!retryUntil) return undefined;
    const timer = setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current >= retryUntil) clearInterval(timer);
    }, 1000);
    return () => clearInterval(timer);
  }, [retryUntil]);

  const run = async (action) => {
    setBusy(action);
    setError('');
    setNotice('');
    try {
      if (action === 'send') {
        const result = await WebApi.resendVerification();
        if (result.verification.alreadyVerified) {
          await onVerified();
        } else {
          setNotice('A verification email is on its way. Use the newest link within 1 hour, then select “Verify my email”.');
          setNow(Date.now());
          setRetryUntil(Date.now() + result.verification.retryAfter * 1000);
        }
      } else {
        const result = await WebApi.me();
        if (result.user.emailVerified) await onVerified();
        else setNotice('Your email is still unverified. Open the latest email and select “Verify my email”, then check again here.');
      }
    } catch (e) {
      setError(e.message || 'Please try again.');
      if (e.retryAfter) {
        setNow(Date.now());
        setRetryUntil(Date.now() + e.retryAfter * 1000);
      }
    } finally {
      setBusy('');
    }
  };

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      <Typography variant="body2" sx={{ mb: 1, overflowWrap: 'anywhere' }}>
        Confirm access to <strong>{email}</strong>. {forTrial
          ? 'Verify your email before starting your free trial. Your trial time starts when you choose to start it after verification.'
          : 'Use a verification link to confirm this account’s email address.'}
      </Typography>
      {initialDelivery && !initialDelivery.sent && initialDelivery.reason !== 'cooldown' && !notice && !error && (
        <Alert severity="warning" sx={{ mb: 1 }}>Your account was created, but we could not send the verification email. Try sending it below, or contact support@pigeonpedigreemanager.com.</Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {notice && <Alert severity="info" role="status" sx={{ mb: 1 }}>{notice}</Alert>}
      <Box display="flex" flexDirection="column" gap={1}>
        <Button variant="contained" onClick={() => run('send')} disabled={Boolean(busy) || remaining > 0}
          startIcon={busy === 'send' ? <CircularProgress size={18} color="inherit" /> : null} sx={{ minHeight: 44 }}>
          {remaining > 0 ? `Resend available in ${remaining}s` : 'Send verification email'}
        </Button>
        <Button variant="outlined" onClick={() => run('check')} disabled={Boolean(busy)}
          startIcon={busy === 'check' ? <CircularProgress size={18} color="inherit" /> : null} sx={{ minHeight: 44 }}>
          I’ve verified — check now
        </Button>
      </Box>
    </Box>
  );
}

export function VerifyEmailPage({ token }) {
  const [busy, setBusy] = useState(false);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState('');

  const verify = async () => {
    setBusy(true);
    setError('');
    try {
      await WebApi.verifyEmail(token);
      // Preserve the unconsumed fragment across refreshes. Remove it only
      // after success, and don't erase a different link opened mid-request.
      if (readVerificationToken() === token) {
        window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
      }
      setVerified(true);
    } catch (e) {
      setError(e.message || 'We could not verify this email. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box display="flex" alignItems="center" justifyContent="center" minHeight="100dvh" p={2} bgcolor="background.default">
      <Card sx={{ width: '100%', maxWidth: 420, borderRadius: 2 }}>
        <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
          <Typography variant="h6" color="primary.main" fontWeight="bold" gutterBottom>Pigeon Pedigree Manager</Typography>
          <Typography variant="h5" component="h1" gutterBottom>{verified ? 'Email verified' : 'Confirm your email'}</Typography>
          {verified ? (
            <>
              <Alert severity="success" role="status" sx={{ mb: 2 }}>Your email has been confirmed. Continue to your account to start your trial.</Alert>
              <Button fullWidth variant="contained" href="/?intent=trial&email-verified=1" sx={{ minHeight: 48 }}>Continue to my account</Button>
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Select the button below to confirm access to the email address used for your account.</Typography>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              <Button fullWidth variant="contained" onClick={verify} disabled={busy}
                startIcon={busy ? <CircularProgress size={18} color="inherit" /> : null} sx={{ minHeight: 48 }}>Verify my email</Button>
              <Button fullWidth href="/?intent=trial&signin=1" disabled={busy} sx={{ mt: 1, minHeight: 44 }}>Sign in to request a new link</Button>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
